import _ from 'lodash';

import {
    Assets,
    Breadcrumb,
    EntryForm,
    HelpBlock,
    InfoPanel,
    Instructions,
    MainMenu,
    Sidebar,
    TitleBlock,
    UserMenu,
} from '@/components';

import * as fieldComponents from '@/fieldTypeComponents';

import {getEntryFields} from '~/components/Comments/Comments.fields';

import {eventBus} from '_/helpers/eventBus';

import {
    mapActions,
    mapGetters,
} from 'vuex';

export default {
    /**
     * Set the component data
     *
     * @return  {object}
     */
    data: function () {
        return {
            commentPublishDateEnd: moment().format(),
            commentPublishDateStart: moment().startOf('day').format(),
            commentInEditingMode: null,
            fields: [],
            formFieldsReset: {
                includeFields: [],
                resetData: null,
            },
            loaderActive: false,
        };
    },

    /**
     * Set the computed properties
     *
     * @type {object}
     */
    computed: {
        ...mapGetters({
            comments: 'comments/comments',
            ui: 'ui/ui',
        }),

        breadcrumbItems() {
            return [{
                text: 'Comment Moderation',
                link: 'comments'
            }];
        },

        dateFormat() {
            return this.ui.photonConfig.dateFormat;
        },

        results() {
            return this.comments.entries;
        },

        pagination() {
            return this.comments.entriesPagination;
        },

        trimmedPagination() {
            let pages = [];
            const currentPage = this.pagination.current_page;
            const totalPages = this.pagination.last_page;
            for (let page = Math.max(1, currentPage - 3); page <= Math.min(currentPage + 3, totalPages); page++) {
                pages.push(page);
            }
            return pages;
        },
    },

    /**
     * Set the methods
     *
     * @type  {Object}
     */
    methods: {
        ...mapActions('ui', [
            'animateWrapper',
            'setBodyClass',
            'setBreadcrumbItems',
            'setTitle'
        ]),

        ...mapActions('comments', [
            'emptyEntries',
            'filterModuleEntries',
            'navigateToPage',
            'submitCommentEntry',
            'updateFilterValue',
        ]),

        filterComments() {
            this.submitSearch();
        },

        endEditingComment() {
            this.commentInEditingMode = null;
        },

        formatDate(entryDate) {
            return entryDate
                ? moment(entryDate).format(this.dateFormat)
                : null;
        },

        /**
         * Returns a localized string from a translatable field object.
         * Falls back to 'en' if the current locale key is missing.
         */
        getLocalizedField(field) {
            if (!field || typeof field !== 'object') {
                return field || '';
            }
            const locale = this.ui.photonConfig.locale || 'en';
            return field[locale] || field['en'] || Object.values(field)[0] || '';
        },

        getCommentAuthor(comment) {
            if (comment.user_users_relation && comment.user_users_relation.length > 0) {
                const user = comment.user_users_relation[0];
                return user.first_name
                    ? `${user.first_name} ${user.last_name || ''}`.trim()
                    : user.email;
            }
            return 'Anonymous';
        },

        getClassName(commentStatus) {
            switch (commentStatus) {
                case 'rejected':
                    return 'alert-danger';
                case 'approved':
                    return 'alert-success';
                case 'flagged':
                    return 'alert-warning';
                default: // pending
                    return 'alert-info';
            }
        },

        getIconName(commentStatus) {
            switch (commentStatus) {
                case 'rejected':
                    return 'fa-ban';
                case 'approved':
                    return 'fa-check';
                case 'flagged':
                    return 'fa-flag';
                default: // pending
                    return 'fa-pause-circle';
            }
        },

        openArticle(slug) {
            if (!slug) return;
            // Resolve the public web app host from a server-injected meta
            // tag (WEB_APP_URL env). The CP runs at api.vuvusport.com, so
            // `'/' + slug` would land on the API host — we want the Next.js
            // article page at vuvusport.com/{locale}/article/{slug}.
            const meta = document.querySelector('meta[name="web-app-url"]');
            const base = (meta && meta.getAttribute('content')) || 'https://vuvusport.com';
            const newWindow = window.open(base + '/en/article/' + slug, '_blank');
            if (newWindow) {
                newWindow.focus();
            }
        },

        goToPage(value) {
            this.navigateToPage({value})
                .then(() => {
                    $(this.$el).find('.pagination li a').blur();
                });
        },

        initEventBusListener() {
            eventBus.$on('fieldTypeChange', payload => {
                if (payload.event.target.type == 'textarea') {
                    return;
                }
                this.updateFilterValue({
                    fieldName: `filter.${payload.name}`,
                    value: payload.value != 0 ? payload.value : '',
                })
                    .then(() => {
                        if (payload.name !== 'title') {
                            this.submitSearch();
                        }
                    });
            });
        },

        processComment({commentId, status, comment}) {
            this.submitCommentEntry({
                id: commentId,
                payload: {
                    system: comment.system,
                    fields: {
                        status: status,
                    },
                },
            }).then(() => {
                this.submitSearch();
            });
        },

        startEditingComment(commentId) {
            this.commentInEditingMode = commentId;
        },

        submitSearch() {
            this.emptyEntries();
            this.loaderActive = true;
            const payload = this.comments.payload;
            return this.filterModuleEntries({payload})
                .then(() => {
                    this.loaderActive = false;
                });
        },

        updateComment(commentId, comment) {
            const body = $(this.$el).find(`#${commentId}commentBody`).val();
            const locale = this.ui.photonConfig.locale || 'en';
            comment.body[locale] = body;
            this.submitCommentEntry({
                id: commentId,
                payload: {
                    system: comment.system,
                    fields: {
                        body: body,
                    },
                },
            }).then(() => {
                this.endEditingComment();
            });
        }
    },

    /**
     * Set the components
     *
     * @type  {Object}
     */
    components: {
        ...fieldComponents,
        Assets,
        Breadcrumb,
        EntryForm,
        HelpBlock,
        InfoPanel,
        Instructions,
        MainMenu,
        Sidebar,
        TitleBlock,
        UserMenu,
    },

    mounted() {
        this.$nextTick(function () {
            this.initEventBusListener();
            this.setBodyClass('admin');
            this.setBreadcrumbItems(this.breadcrumbItems);
            this.animateWrapper();
            this.fields = getEntryFields(this);
            this.submitSearch();
        });
    },

    beforeDestroy() {
        eventBus.$off('fieldTypeChange');
    },
};
